<template>
  <div v-show="!isEmpty(apData.snapshotDoc)">
    <el-row type="flex" class="title-bar" justify="space-between">
      <div>
        <span class="tit content-title">무선 AP</span>
        <span class="condition-text">({{ apDateLabel }})</span>
      </div>
      <div>
        <el-radio-group class="mr-r5" v-model="apType">
          <el-radio label="auth">인가</el-radio>
          <el-radio label="rogue">비인가</el-radio>
        </el-radio-group>
        <w-header-search-button size="mini" @click="onShowApSearchToolClick"></w-header-search-button>
      </div>
    </el-row>
    <el-table-wrapper
      v-show="apType === 'auth'"
      :data="apData.snapshotDoc ? apData.snapshotDoc.auth : []"
      :columns="authApColumns"
      :show-custom-header="true"
      :show-search-tool="showAuthApSearchTool"
      :show-overflow-tooltip="true"
      :pagination="false"
      :column-default="columnDefault"
    >
      <template slot-scope="scope" slot="cpu-slot">
        <w-rsc-perf-event-progress-bar v-if="!isNil(scope.row['DEVICE_PERF/MEM_USERATE'])" :perfRecords="scope.row" perfKey="DEVICE_PERF/MEM_USERATE" />
        <span v-else>{{ EMPTY_TEXT }}</span>
      </template>
      <template slot-scope="scope" slot="memory-slot">
        <w-rsc-perf-event-progress-bar v-if="!isNil(scope.row['DEVICE_PERF/MEM_USERATE'])" :perfRecords="scope.row" perfKey="DEVICE_PERF/MEM_USERATE" />
        <span v-else>{{ EMPTY_TEXT }}</span>
      </template>
      <template slot-scope="scope" slot="icmp-slot">
        <span> {{ convertSlotValue(scope.row['RESPONSETIME/RESPONSETIME_ICMP']) }}</span>
      </template>
      <template slot-scope="scope" slot="snmp-slot">
        <span> {{ convertSlotValue(scope.row['RESPONSETIME/RESPONSETIME_SNMP']) }}</span>
      </template>
    </el-table-wrapper>
    <el-table-wrapper
      v-show="apType === 'rogue'"
      :data="apData.snapshotDoc ? apData.snapshotDoc.rogue : []"
      :columns="rogueApColumns"
      :show-custom-header="true"
      :show-search-tool="showRogueApSearchTool"
      :show-overflow-tooltip="true"
      :column-default="columnDefault"
    />
  </div>
</template>

<script>
import { isNil, isEmpty, concat } from 'lodash';
import { isTrue } from '@/utils/boolean';
import { getValueAfterCheckEmpty, EMPTY_TEXT } from '@/utils/string';
import { getApStatusName, showApSnr, showRogueApStatus, getRogueApStatus } from '@/utils/wireless-device';
import { convertValuByUnit } from '@/utils/number';
import { columnDefault } from '@/constants/table';
import { getDateLabel } from '../../../../utils/fault-analysis';
import { etcInfoDefine } from '../../../../constants/fault-analysis';

export default {
  name: 'WWirelessTable',
  props: {
    /**
     * 기타 분석 정보 조회 시 필요한 정보 객체 (동시성 문제 고려하여 하나의 객체로 전달)
     * 1) obj: 분석 대상 장비 정보
     * 2) eventHistData: 분석 이벤트 이력 정보
     * 3) etcInfo: 기타 분석 정보
     * @type {Object}
     */
    data: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      columnDefault,
      apType: 'auth',
      showAuthApSearchTool: false,
      showRogueApSearchTool: false,
      apData: {},
      authApColumns: [],
      rogueApColumns: [],
      authApBaseColumns: [
        {
          prop: 'objName',
          label: '장비명',
          remoteProp: 'o.obj_name',
          sortable: true,
          searchable: true,
          formatter: (row, column, value) => getValueAfterCheckEmpty(value),
        },
        {
          prop: 'ipaddress',
          label: 'IP',
          sortable: true,
          searchable: true,
          formatter: (row, column, value) => getValueAfterCheckEmpty(value),
        },
        {
          prop: 'objMacaddress',
          label: 'MAC',
          remoteProp: 'o.obj_macaddress',
          sortable: true,
          searchable: true,
          formatter: (row, column, value) => getValueAfterCheckEmpty(value),
        },
        {
          prop: 'objCompanyName',
          label: '제조사',
          remoteProp: 'oc.obj_company_name',
          sortable: true,
          searchable: true,
          formatter: (row, column, value) => getValueAfterCheckEmpty(value),
        },
        {
          prop: 'ssid',
          label: 'WLAN',
          remoteProp: 'ncmInfo.ssid',
          sortable: true,
          searchable: true,
          formatter: (row, column, value) => getValueAfterCheckEmpty(value),
        },
        {
          prop: 'radioBand',
          label: '주파수 대역',
          remoteProp: 'ncmInfo.radioBand',
          sortable: true,
          searchable: true,
          formatter: (row, column, value) => getValueAfterCheckEmpty(value),
        },
        {
          prop: 'apGroup',
          label: 'AP 그룹',
          remoteProp: 'ncmInfo.apGroup',
          sortable: true,
          searchable: true,
          formatter: (row, column, value) => getValueAfterCheckEmpty(value),
        },
        {
          prop: 'WIRELESS_ACCESSPOINT/AP_STATUS',
          label: '상태',
          sortable: true,
          searchable: true,
          formatter: (row, column, value) => getApStatusName(value),
        },
        {
          prop: 'WIRELESS_ACCESSPOINT/CLIENT_COUNT',
          label: '접속자 수',
          sortable: true,
          searchable: false,
          formatter: (row, column, value) => getValueAfterCheckEmpty(value),
        },
        {
          label: '트래픽',
          headerAlign: 'center',
          childColumns: [
            {
              prop: 'WIRELESS_ACCESSPOINT/AP_TRAFFIC_BPS_IN',
              label: 'IN',
              sortable: true,
              headerAlign: 'center',
              formatter: (row, column, value) => convertValuByUnit(value, 'bps', true, true, true),
              width: 80,
            },
            {
              prop: 'WIRELESS_ACCESSPOINT/AP_TRAFFIC_BPS_OUT',
              label: 'OUT',
              sortable: true,
              headerAlign: 'center',
              formatter: (row, column, value) => convertValuByUnit(value, 'bps', true, true, true),
              width: 80,
            },
          ],
        },
      ],
      rogueApBaseColumns: [
        {
          prop: 'bssid',
          label: 'bssid',
          sortable: true,
          searchable: true,
          formatter: (row, column, value) => getValueAfterCheckEmpty(value),
        },
        {
          prop: 'essid',
          label: 'essid',
          sortable: true,
          searchable: true,
          formatter: (row, column, value) => getValueAfterCheckEmpty(value),
        },
        {
          prop: 'radioBand',
          label: '주파수 대역',
          sortable: true,
          searchable: true,
          formatter: (row, column, value) => getValueAfterCheckEmpty(value),
        },
        {
          prop: 'channel',
          label: '채널',
          sortable: true,
          searchable: true,
          formatter: (row, column, value) => getValueAfterCheckEmpty(value),
        },
      ],
    };
  },
  computed: {
    apDateLabel() {
      return isNil(this.apData.timestamp) ? EMPTY_TEXT : getDateLabel(this.apData.timestamp);
    },
  },
  watch: {
    data: {
      immediate: true,
      handler(to) {
        this.reset();
        if (!isNil(to)) {
          this.setData();
        }
      },
    },
  },
  methods: {
    isTrue,
    isEmpty,
    /**
     * AP 스냅샷을 설정한다.
     * @author Ju
     */
    setData() {
      if (!isEmpty(this.data.etcInfo) && !isEmpty(this.data.etcInfo[etcInfoDefine.WIRELESS_AP])) {
        this.apData = this.data.etcInfo[etcInfoDefine.WIRELESS_AP];

        if (showRogueApStatus(this.data.obj.objCompanyId)) {
          const statusColumn = {
            prop: 'status',
            label: '상태',
            sortable: true,
            searchable: true,
            formatter: this.getConvertStatusValue,
          };
          this.rogueApColumns = concat(this.rogueApBaseColumns, [statusColumn]);
        } else {
          this.rogueApColumns = this.rogueApBaseColumns;
        }

        if (showApSnr(this.data.obj.objCompanyId)) {
          const snrColumn = {
            prop: 'WIRELESS_ACCESSPOINT/AP_SNR',
            label: 'SNR',
            sortable: true,
            searchable: false,
            formatter: (row, column, value) => convertValuByUnit(value, 'dBm', true, true, true),
          };
          this.authApColumns = concat(this.authApBaseColumns, [snrColumn]);
        } else {
          this.authApColumns = this.authApBaseColumns;
        }
      }
    },
    /**
     * 화면을 초기화한다.
     * @author Ju
     */
    reset() {
      this.rogueApColumns = [];
      this.authApColumns = [];
    },
    /**
     * 비인가 AP인 경우 치환 가능한 값은 UP/DOWN, 치환 불가한 값은 그대로 표현하도록 하는 formmater
     * @author yelim
     * @param  {[type]} row    [description]
     * @param  {[type]} column [description]
     * @param  {[type]} value  [description]
     * @return {[type]}        [description]
     */
    getConvertStatusValue(row, column, value) {
      return getRogueApStatus(value);
    },
    /**
     * 검색 버튼 이벤트 핸들러
     * @author Ju
     * @return {[type]} [description]
     */
    onShowApSearchToolClick() {
      if (this.apType === 'auth') {
        this.showAuthApSearchTool = !this.showAuthApSearchTool;
      } else {
        this.showRogueApSearchTool = !this.showRogueApSearchTool;
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/popup/FaultAnalysisPopup/components/FaultAnalysisInfoView/EtcInfo/NetdeviceEtcInfoView/WirelessApTable.vue